import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError, photoUrl } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { X, Ban, EyeOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [open, setOpen] = useState([]);
  const [resolved, setResolved] = useState([]);

  const load = async () => {
    try {
      const [o, r] = await Promise.all([
        api.get("/admin/reports", { params: { status: "open" } }),
        api.get("/admin/reports", { params: { status: "resolved" } }),
      ]);
      setOpen(o.data); setResolved(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id, action) => {
    try { await api.post(`/admin/reports/${id}/resolve?action=${action}`); toast.success(`Report ${action}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <PageHeader title="Reports Queue" subtitle="Review and act on user reports" />
      <Tabs defaultValue="open">
        <TabsList>
          <TabsTrigger value="open" data-testid="admin-tab-open">Open ({open.length})</TabsTrigger>
          <TabsTrigger value="resolved" data-testid="admin-tab-resolved">Resolved ({resolved.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="open" className="mt-6"><List reports={open} onAction={resolve} canAct /></TabsContent>
        <TabsContent value="resolved" className="mt-6"><List reports={resolved} onAction={() => {}} /></TabsContent>
      </Tabs>
    </div>
  );
}

function List({ reports, onAction, canAct }) {
  if (!reports.length) return <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50/50"><AlertTriangle className="w-12 h-12 text-slate-300 mb-3" /><p className="text-slate-500">No reports here.</p></div>;
  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <div key={r.report_id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 sm:p-5" data-testid={`report-${r.report_id}`}>
          <div className="flex flex-wrap items-start gap-4">
            {r.target?.photos?.[0] && <img src={photoUrl(r.target.photos[0])} alt="" className="w-16 h-20 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-800">{r.target?.name || "Unknown"}</span>
                <span className="text-[10px] uppercase tracking-wider bg-red-50 text-red-600 px-2 py-0.5 rounded-full">{r.reason}</span>
                <span className="text-xs text-slate-400">· {new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Reported by: {r.reporter?.name} ({r.reporter?.email})</div>
              {r.note && <p className="text-sm mt-2 bg-slate-50 rounded-lg p-3 italic text-slate-600">"{r.note}"</p>}
              {r.resolution && <div className="text-xs mt-2 text-emerald-700">Resolved: {r.resolution}</div>}
            </div>
            {canAct && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => onAction(r.report_id, "dismiss")} data-testid={`dismiss-${r.report_id}`}><X className="w-3 h-3 mr-1" />Dismiss</Button>
                <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => onAction(r.report_id, "hide_user")} data-testid={`hide-${r.report_id}`}><EyeOff className="w-3 h-3 mr-1" />Hide</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => onAction(r.report_id, "ban_user")} data-testid={`ban-${r.report_id}`}><Ban className="w-3 h-3 mr-1" />Ban</Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
